(() => {
  const config = window.autoCartBundlesConfig || {};
  const managedPropertyKey = '_auto_bundle_managed';
  const managedTypeKey = '_auto_bundle_type';
  const testerSelectionAttributeKeys = {
    primary_tester: '_auto_bundle_primary_tester__',
    multibalm_tester: '_auto_bundle_multibalm_tester__',
    multitasker_tester: '_auto_bundle_multitasker_tester__',
    lip_gloss_tester: '_auto_bundle_lip_gloss_tester__',
    cc_tester: '_auto_bundle_cc_tester__',
  };
  const syncDelay = 250;

  let syncTimeout;
  let syncInFlight = null;
  let syncRequested = false;

  function isThresholdItem(item) {
    return item?.properties?._auto_threshold_product === '1';
  }

  function isManagedItem(item) {
    return item?.properties?.[managedPropertyKey] === '1';
  }

  function getConfiguredManagedType(item) {
    const variantId = Number(item?.variant_id || item?.id);

    if (!variantId) return null;

    const configuredTesterTypes = [
      ['multibalm_tester', config.multibalmVariants || []],
      ['multitasker_tester', config.multitaskerVariants || []],
      ['lip_gloss_tester', config.lipGlossVariants || []],
      ['cc_tester', config.ccTesterVariants || []],
    ];

    const configuredTester = configuredTesterTypes.find(([, variants]) =>
      variants.some((variant) => Number(variant.id) === variantId)
    );

    if (configuredTester) {
      const declaredType = item?.properties?.[managedTypeKey];

      // A primary tester is selected from the multitasker/gloss lists.
      if (declaredType === 'primary_tester') return declaredType;
      return configuredTester[0];
    }

    const packagingVariantIds = [
      config.smallPackagingVariant?.id,
      config.largePackagingVariant?.id,
    ].filter(Boolean);

    if (packagingVariantIds.some((id) => Number(id) === variantId)) return 'packaging';
    return null;
  }

  function getManagedType(item) {
    if (isManagedItem(item) && item.properties?.[managedTypeKey]) {
      return item.properties[managedTypeKey];
    }

    // Reserved testers and packaging must stay managed even when somebody adds
    // their variant directly and omits the private line-item properties.
    return getConfiguredManagedType(item);
  }

  function isControlledItem(item) {
    return config.enabled && Boolean(getManagedType(item));
  }

  async function removeBeautyKeeperWithoutLipPencil(cart) {
    const items = cart.items || [];
    const hasLipPencil = items.some((item) =>
      (item.product_title || item.title || '').startsWith('LIP PENCIL with peptides')
    );
    if (hasLipPencil) return { cart, changed: false };

    const keeperVariantIds = [60041667641630, 60041667674398];
    const keeperItems = items.filter((item) =>
      keeperVariantIds.includes(Number(item.variant_id || item.id))
    );

    // Line keys also cover duplicate variants with different bundle properties.
    for (const item of keeperItems) {
      cart = await changeCartItem(item.key, 0);
    }
    return { cart, changed: keeperItems.length > 0 };
  }

  async function removeLoneBundlePackaging(cart) {
    const packagingHandles = [
      'podarunkove-pakuvannya-unico-x-iryna-maksymova',
    ];
    const visibleItems = getVisibleItems(cart);
    if (!visibleItems.length || !visibleItems.every((item) => packagingHandles.includes(item.handle))) {
      return { cart, changed: false };
    }

    // Remove by line key: different variants or bundle properties can create
    // multiple lines for the same product. Keep unrelated cart lines intact.
    for (const item of visibleItems) {
      cart = await changeCartItem(item.key, 0);
    }
    return { cart, changed: true };
  }

  function getVisibleItems(cart) {
    return (cart.items || []).filter((item) => !isControlledItem(item) && !isThresholdItem(item));
  }

  function getVisibleQuantity(cart) {
    return getVisibleItems(cart).reduce((total, item) => total + item.quantity, 0);
  }

  function pickSelectedVariant(variants, cart, type) {
    const attributeKey = testerSelectionAttributeKeys[type];
    const selectedVariantId = cart.attributes?.[attributeKey];
    const selectedVariant = variants.find(
      (variant) => variant.available && String(variant.id) === String(selectedVariantId)
    );

    return selectedVariant || variants.find((variant) => variant.available) || null;
  }

  function isConfiguredProduct(item, productIds) {
    return productIds.includes(item.product_id);
  }

  function buildManagedProperties(type) {
    if (type === 'threshold_product') {
      return { _auto_threshold_product: '1', GIFT: config.thresholdVariant.title };
    }
    return {
      [managedPropertyKey]: '1',
      [managedTypeKey]: type,
    };
  }

  function getDesiredManagedItems(cart) {
    const visibleItems = getVisibleItems(cart);
    const visibleQuantity = getVisibleQuantity(cart);

    if (!visibleQuantity) return [];

    const hasCcProduct = visibleItems.some((item) => isConfiguredProduct(item, config.ccProductIds || []));
    const desiredItems = [];

    const multibalmVariant = pickSelectedVariant(
      config.multibalmVariants || [],
      cart,
      'multibalm_tester'
    );
    const multitaskerVariant = pickSelectedVariant(
      config.multitaskerVariants || [],
      cart,
      'multitasker_tester'
    );
    const lipGlossVariant = pickSelectedVariant(
      config.lipGlossVariants || [],
      cart,
      'lip_gloss_tester'
    );
    const ccTesterVariant = pickSelectedVariant(
      config.ccTesterVariants || [],
      cart,
      'cc_tester'
    );
    const primaryTesterVariant = pickSelectedVariant(
      [...(config.multitaskerVariants || []), ...(config.lipGlossVariants || [])],
      cart,
      'primary_tester'
    );

    if (multibalmVariant) {
      desiredItems.push({
        type: 'multibalm_tester',
        variantId: multibalmVariant.id,
        quantity: 1,
      });
    }

    if (hasCcProduct) {
      if (multitaskerVariant) {
        desiredItems.push({
          type: 'multitasker_tester',
          variantId: multitaskerVariant.id,
          quantity: 1,
        });
      }

      if (lipGlossVariant) {
        desiredItems.push({
          type: 'lip_gloss_tester',
          variantId: lipGlossVariant.id,
          quantity: 1,
        });
      }
    } else {
      if (primaryTesterVariant) {
        desiredItems.push({
          type: 'primary_tester',
          variantId: primaryTesterVariant.id,
          quantity: 1,
        });
      }

      if (ccTesterVariant) {
        desiredItems.push({
          type: 'cc_tester',
          variantId: ccTesterVariant.id,
          quantity: 1,
        });
      }
    }

    const packagingVariant = visibleQuantity >= 5 ? config.largePackagingVariant : config.smallPackagingVariant;
    const hasPinkShopper = (cart.items || []).some((item) => item.handle === 'unico-pink-shopper');
    const skipWhitePackaging = hasPinkShopper && packagingVariant?.id === config.largePackagingVariant?.id;

    if (packagingVariant?.id && packagingVariant.available && !skipWhitePackaging) {
      desiredItems.push({
        type: 'packaging',
        variantId: packagingVariant.id,
        quantity: 1,
      });
    }

    return desiredItems;
  }

  async function syncThresholdProduct(cart) {
    const variant = config.thresholdVariant;
    const isSelectedProduct = (item) => Boolean(
      (variant?.productId && Number(item.product_id) === Number(variant.productId)) ||
      (variant?.id && Number(item.variant_id) === Number(variant.id))
    );
    const thresholdActive = config.thresholdEnabled && Number(config.thresholdAmount) > 0;
    const subtotal = (cart.items || [])
      .filter((item) => !isSelectedProduct(item) && !isThresholdItem(item) && !isManagedItem(item) && !isControlledItem(item))
      .reduce((sum, item) => sum + item.final_line_price, 0);
    const belowThreshold = thresholdActive && subtotal < Number(config.thresholdAmount);
    const existing = (cart.items || []).filter((item) =>
      isThresholdItem(item) || (belowThreshold && isSelectedProduct(item))
    );
    const alreadyInCart = (cart.items || []).some((item) =>
      !isThresholdItem(item) && isSelectedProduct(item)
    );
    const eligible = thresholdActive &&
      subtotal >= Number(config.thresholdAmount) && variant?.id && variant.available && !alreadyInCart;
    const keep = eligible ? existing.find((item) => item.variant_id === variant.id) : null;
    let changed = false;

    for (const item of existing) {
      if (item !== keep) {
        await changeCartItem(item.key, 0);
        changed = true;
      } else if (item.quantity !== 1 || item.properties?.GIFT !== variant.title) {
        await changeCartItem(item.key, 1, { ...item.properties, GIFT: variant.title });
        changed = true;
      }
    }

    if (thresholdActive && !belowThreshold) {
      for (const item of (cart.items || []).filter((item) => !isThresholdItem(item) && isSelectedProduct(item))) {
        if (item.properties?.GIFT !== variant.title) {
          await changeCartItem(item.key, item.quantity, { ...item.properties, GIFT: variant.title });
          changed = true;
        }
      }
    }

    if (eligible && !keep) {
      await addManagedItems([{ type: 'threshold_product', variantId: variant.id, quantity: 1 }]);
      changed = true;
    }

    return { cart: changed ? await fetchCart() : cart, changed };
  }

  async function fetchCart() {
    const response = await fetch(`${window.routes.cart_url}.js`, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Cart fetch failed: ${response.status}`);
    }

    return response.json();
  }

  async function changeCartItem(itemKey, quantity, properties) {
    const requestConfig = fetchConfig();

    const response = await fetch(window.routes.cart_change_url, {
      ...requestConfig,
      headers: {
        ...requestConfig.headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: itemKey,
        quantity,
        ...(properties ? { properties } : {}),
      }),
    });

    if (!response.ok) {
      throw new Error(`Cart change failed: ${response.status}`);
    }

    return response.json();
  }

  async function addManagedItems(items) {
    if (!items.length) return null;

    const requestConfig = fetchConfig('javascript');

    const response = await fetch(window.routes.cart_add_url, {
      ...requestConfig,
      headers: {
        ...requestConfig.headers,
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: items.map((item) => ({
          id: item.variantId,
          quantity: item.quantity,
          properties: buildManagedProperties(item.type),
        })),
      }),
    });

    if (!response.ok) {
      throw new Error(`Cart add failed: ${response.status}`);
    }

    return response.json();
  }

  async function saveTesterSelection(type, variantId) {
    const attributeKey = testerSelectionAttributeKeys[type];
    if (!attributeKey) return null;

    const requestConfig = fetchConfig();
    const response = await fetch(window.routes.cart_update_url, {
      ...requestConfig,
      headers: {
        ...requestConfig.headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          [attributeKey]: String(variantId),
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Tester selection update failed: ${response.status}`);
    }

    return response.json();
  }

  function mapManagedItems(cart) {
    return (cart.items || [])
      .filter(isControlledItem)
      .reduce((accumulator, item) => {
        const type = getManagedType(item);

        if (!type) return accumulator;

        if (!accumulator[type]) accumulator[type] = [];
        accumulator[type].push(item);
        return accumulator;
      }, {});
  }

  function updateTesterPreview(select) {
    const image = select.closest('.auto-cart-tester-picker__field')?.querySelector(
      '[data-auto-bundle-tester-image]'
    );
    if (!image) return;

    const selectedOption = select.selectedOptions?.[0];
    const imageUrl = selectedOption?.dataset.testerImage;

    if (!imageUrl) {
      image.removeAttribute('src');
      image.hidden = true;
      return;
    }

    image.src = imageUrl;
    image.alt = selectedOption.dataset.testerImageAlt || selectedOption.textContent.trim();
    image.hidden = false;
  }

  function syncTesterSelectors(cart) {
    const managedByType = mapManagedItems(cart);

    document.querySelectorAll('[data-auto-bundle-tester-select]').forEach((select) => {
      const type = select.dataset.testerType;
      const attributeKey = testerSelectionAttributeKeys[type];
      const selectedVariantId = cart.attributes?.[attributeKey] || managedByType[type]?.[0]?.variant_id;

      if (selectedVariantId && select.querySelector(`option[value="${CSS.escape(String(selectedVariantId))}"]`)) {
        select.value = String(selectedVariantId);
      }

      updateTesterPreview(select);
    });
  }

  function buildSyncPlan(cart, desiredItems) {
    const desiredByType = desiredItems.reduce((accumulator, item) => {
      accumulator[item.type] = item;
      return accumulator;
    }, {});

    const existingByType = mapManagedItems(cart);
    const removals = [];
    const quantityUpdates = [];
    const additions = [];

    Object.entries(existingByType).forEach(([type, items]) => {
      const desiredItem = desiredByType[type];

      if (!desiredItem) {
        items.forEach((item) => removals.push(item));
        return;
      }

      const matchingItem =
        items.find(
          (item) =>
            item.variant_id === desiredItem.variantId &&
            isManagedItem(item) &&
            item.properties?.[managedTypeKey] === type
        ) || items.find((item) => item.variant_id === desiredItem.variantId);

      items.forEach((item) => {
        if (!matchingItem || item.key !== matchingItem.key) {
          removals.push(item);
        }
      });

      if (matchingItem) {
        if (
          matchingItem.quantity !== desiredItem.quantity ||
          !isManagedItem(matchingItem) ||
          matchingItem.properties?.[managedTypeKey] !== type
        ) {
          quantityUpdates.push({
            key: matchingItem.key,
            quantity: desiredItem.quantity,
            properties: buildManagedProperties(type),
          });
        }
      } else {
        additions.push(desiredItem);
      }
    });

    desiredItems.forEach((item) => {
      if (!existingByType[item.type]) {
        additions.push(item);
      }
    });

    return { removals, quantityUpdates, additions };
  }

  async function applyManagedSync(cart, desiredItems) {
    const { removals, quantityUpdates, additions } = buildSyncPlan(cart, desiredItems);
    const changed = Boolean(removals.length || quantityUpdates.length || additions.length);

    if (!changed) return { cart, changed: false };

    for (const item of removals) {
      await changeCartItem(item.key, 0);
    }

    for (const update of quantityUpdates) {
      await changeCartItem(update.key, update.quantity, update.properties);
    }

    if (additions.length) {
      await addManagedItems(additions);
    }

    return { cart: await fetchCart(), changed: true };
  }

  async function refreshDrawerSection() {
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    if (!cartDrawerWrapper) return;

    const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const targetElement = document.querySelector('#CartDrawer .drawer__inner');
    const sourceElement = html.querySelector('#CartDrawer .drawer__inner');

    if (targetElement && sourceElement) {
      targetElement.replaceWith(sourceElement);
    }

    const renderedCartDrawer = html.querySelector('cart-drawer');
    cartDrawerWrapper.classList.toggle('is-empty', renderedCartDrawer?.classList.contains('is-empty'));
  }

  async function refreshMainCartSections() {
    const mainCartItems = document.querySelector('cart-items');

    if (mainCartItems) {
      const response = await fetch(`${window.routes.cart_url}?section_id=main-cart-items`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const sourceCartItems = html.querySelector('cart-items');

      if (sourceCartItems) {
        mainCartItems.innerHTML = sourceCartItems.innerHTML;
        mainCartItems.className = sourceCartItems.className;
      }
    }

    const mainCartFooter = document.getElementById('main-cart-footer');

    if (mainCartFooter) {
      const sectionId = mainCartFooter.dataset.id;
      const response = await fetch(`${window.routes.cart_url}?section_id=${sectionId}`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const renderedFooter = html.getElementById('main-cart-footer');

      if (renderedFooter) {
        mainCartFooter.innerHTML = renderedFooter.innerHTML;
        mainCartFooter.className = renderedFooter.className;
      }
    }
  }

  // Theme renders from cart/change or cart/add responses can land after our own
  // refresh and paint a cart without the auto-added lines. Compare the rendered
  // rows and quantities with Shopify's cart (managed lines are hidden by the
  // Liquid templates).
  function isRenderedCartStale(cart) {
    const expectedRows = (cart.items || [])
      .map((item, index) => (isManagedItem(item) ? null : `${index + 1}x${item.quantity}`))
      .filter(Boolean)
      .sort()
      .join(',');
    const renderedGroups = [
      ['#CartDrawer', 'tr[id^="CartDrawer-Item-"]', 'CartDrawer-Item-', 'Drawer-quantity-'],
      ['#main-cart-items', 'tr[id^="CartItem-"]', 'CartItem-', 'Quantity-'],
    ];

    return renderedGroups.some(([containerSelector, rowSelector, rowPrefix, quantityPrefix]) => {
      const container = document.querySelector(containerSelector);
      if (!container) return false;

      const renderedRows = [...container.querySelectorAll(rowSelector)]
        .map((row) => {
          const index = row.id.slice(rowPrefix.length);
          const quantityInput = row.querySelector(`[id="${quantityPrefix}${index}"]`);
          return `${index}x${quantityInput?.getAttribute('value') ?? ''}`;
        })
        .sort()
        .join(',');
      return renderedRows !== expectedRows;
    });
  }

  async function refreshRenderedCart() {
    await Promise.all([refreshDrawerSection(), refreshMainCartSections()]);
    const cartBubble = document.getElementById('cart-icon-bubble');
    if (cartBubble) {
      const response = await fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`);
      if (!response.ok) throw new Error(`Cart icon refresh failed: ${response.status}`);
      cartBubble.innerHTML = await response.text();
    }
  }

  async function synchronizeCart(cart = null) {
    if (syncInFlight) {
      syncRequested = true;
      return syncInFlight;
    }

    const endCheckoutBusy = window.cartCheckoutBusy?.begin();
    syncInFlight = (async () => {
      // Event payloads can be older than a preceding add/change request. Always
      // reconcile against Shopify's current cart to prevent duplicate additions.
      const initialCart = await fetchCart();
      const keeperResult = await removeBeautyKeeperWithoutLipPencil(initialCart);
      const packagingResult = await removeLoneBundlePackaging(keeperResult.cart);
      // Resolve the gift first so packaging sees the shopper added or removed in this sync.
      const thresholdResult = await syncThresholdProduct(packagingResult.cart);
      const remainingCart = thresholdResult.cart;
      syncTesterSelectors(remainingCart);
      const managedResult = config.enabled
        ? await applyManagedSync(remainingCart, getDesiredManagedItems(remainingCart))
        : { cart: remainingCart, changed: false };
      const updatedCart = managedResult.cart;
      const changed = keeperResult.changed || packagingResult.changed || managedResult.changed || thresholdResult.changed;

      if (!changed) {
        if (isRenderedCartStale(updatedCart)) await refreshRenderedCart();
        return null;
      }

      syncTesterSelectors(updatedCart);
      await refreshRenderedCart();
      syncTesterSelectors(updatedCart);

      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

      return updatedCart;
    })()
      .catch((error) => {
        console.error('Auto cart bundles sync failed:', error);
        return null;
      })
      .finally(() => {
        syncInFlight = null;
        endCheckoutBusy?.();
        if (syncRequested) {
          syncRequested = false;
          scheduleSync();
        }
      });

    return syncInFlight;
  }

  function scheduleSync(cart = null) {
    window.clearTimeout(syncTimeout);
    syncTimeout = window.setTimeout(() => {
      synchronizeCart(cart);
    }, syncDelay);
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-auto-bundle-tester-select]').forEach(updateTesterPreview);
    scheduleSync();
  });
  window.addEventListener('pageshow', scheduleSync);

  document.addEventListener('change', async (event) => {
    if (!config.enabled) return;
    const select = event.target.closest?.('[data-auto-bundle-tester-select]');
    if (!select) return;
    updateTesterPreview(select);

    const matchingSelectors = document.querySelectorAll(
      `[data-auto-bundle-tester-select][data-tester-type="${select.dataset.testerType}"]`
    );
    matchingSelectors.forEach((matchingSelect) => {
      matchingSelect.disabled = true;
    });

    try {
      await saveTesterSelection(select.dataset.testerType, select.value);
      const existingSync = syncInFlight;
      await synchronizeCart();

      if (existingSync) {
        await synchronizeCart();
      }
    } catch (error) {
      console.error('Tester selection failed:', error);
      syncTesterSelectors(await fetchCart());
    } finally {
      matchingSelectors.forEach((matchingSelect) => {
        matchingSelect.disabled = false;
      });
    }
  });

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event = {}) => {
    if (event.source === 'auto-cart-bundles') return Promise.resolve();

    if (event.source === 'product-form' || event.source === 'quick-add') {
      scheduleSync();
      return Promise.resolve();
    }

    return synchronizeCart(event.cartData || null);
  });

  document.addEventListener('click', (event) => {
    const removeButton = event.target.closest?.('cart-remove-button');
    if (!removeButton) return;

    window.clearTimeout(syncTimeout);
  });

  document.addEventListener('submit', (event) => {
    const cartForm = event.target.closest?.('form[action*="/cart"]');
    if (!cartForm) return;

    window.clearTimeout(syncTimeout);
  });

  document.addEventListener('change', (event) => {
    const quantityInput = event.target.closest?.('.quantity__input');
    if (!quantityInput) return;

    window.clearTimeout(syncTimeout);
  });
})();
